import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, Camera, CheckCircle, XCircle, Upload, ArrowRight, Pencil, Save, X, Database, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface CourthouseSheetScannerProps {
  county: string;
  countyLabel: string;
  onRecordsUpdated?: () => void;
  onViewUpdatedRecords?: () => void;
}

interface ExtractedRow {
  row_number: number;
  filing_date: string;
  doc_number?: string;
  seller_name: string;
  address: string | null;
  crossed_out?: boolean;
  status: 'ready' | 'saved' | 'removed' | 'error' | 'unmatched';
  errorMessage?: string;
}

const DOC_MATCH_COUNTIES = ['delta', 'menominee'];

export function CourthouseSheetScanner({ county, countyLabel, onRecordsUpdated, onViewUpdatedRecords }: CourthouseSheetScannerProps) {
  const [imageData, setImageData] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [rows, setRows] = useState<ExtractedRow[]>([]);
  const [totalSaved, setTotalSaved] = useState(0);
  const [sheetsScanned, setSheetsScanned] = useState(0);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editField, setEditField] = useState<'seller_name' | 'address' | 'filing_date' | 'doc_number'>('address');
  const [editValue, setEditValue] = useState("");
  const [integrated, setIntegrated] = useState(false);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => setImageData(event.target?.result as string);
    reader.readAsDataURL(file);
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf("image") !== -1) {
        const blob = items[i].getAsFile();
        if (blob) {
          const reader = new FileReader();
          reader.onload = (event) => setImageData(event.target?.result as string);
          reader.readAsDataURL(blob);
        }
      }
    }
  };

  const handleScan = async () => {
    if (!imageData) return;
    setIsProcessing(true);
    setRows([]);
    setIntegrated(false);

    try {
      const { data, error } = await supabase.functions.invoke("scan-courthouse-sheet", {
        body: { imageData, county },
      });

      if (error) throw error;

      if (data.success && data.rows) {
        const extracted: ExtractedRow[] = data.rows.map((r: any) => ({
          row_number: r.row_number,
          filing_date: r.filing_date || '',
          doc_number: r.doc_number ? String(r.doc_number).replace(/\s+/g, '') : undefined,
          seller_name: r.seller_name || '',
          address: r.address || null,
          crossed_out: !!r.crossed_out,
          status: r.crossed_out ? ('removed' as const) : ('ready' as const),
        }));
        setRows(extracted);
        setSheetsScanned(prev => prev + 1);
        toast.success(`Sheet ${sheetsScanned + 1}: Extracted ${extracted.length} rows — review and save`);
      } else {
        throw new Error(data.error || "Scan failed");
      }
    } catch (error: any) {
      console.error("Scan error:", error);
      toast.error(error.message || "Failed to scan sheet");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSaveAll = async () => {
    const isDocMatch = DOC_MATCH_COUNTIES.includes(county.toLowerCase());

    if (isDocMatch) {
      // Delta / Menominee: match existing records by document_number (liber-page) and update grantor_address only
      const readyRows = rows.filter(r => r.status === 'ready' && r.doc_number && r.address);
      if (readyRows.length === 0) {
        toast.error("No rows with both a doc # and address to save");
        return;
      }
      setIsSaving(true);
      try {
        let matched = 0;
        let unmatched = 0;
        const updated = [...rows];

        for (let idx = 0; idx < updated.length; idx++) {
          const r = updated[idx];
          if (r.status !== 'ready' || !r.doc_number || !r.address) continue;

          const docNum = r.doc_number.replace(/\s+/g, '').trim();

          const { data: existing } = await supabase
            .from('county_research')
            .select('id, grantor_name, filing_date, document_type')
            .eq('county', county.toLowerCase())
            .eq('document_number', docNum)
            .is('deleted_at', null)
            .limit(1);

          if (existing && existing.length > 0) {
            const match = existing[0];
            const { error: updErr } = await supabase
              .from('county_research')
              .update({
                grantor_address: r.address,
                address_extraction_method: 'courthouse_handwritten',
                address_needs_review: false,
                status: 'reviewed',
              })
              .eq('id', match.id);
            if (updErr) {
              console.error(`Update error for doc "${docNum}":`, updErr);
              updated[idx] = { ...r, status: 'error', errorMessage: updErr.message };
            } else {
              updated[idx] = {
                ...r,
                status: 'saved',
                seller_name: match.grantor_name || r.seller_name,
                filing_date: match.filing_date || r.filing_date,
              };
              matched++;
            }
          } else {
            updated[idx] = { ...r, status: 'unmatched', errorMessage: 'No record found for this doc #' };
            unmatched++;
          }
        }

        setRows(updated);
        setTotalSaved(prev => prev + matched);
        if (matched > 0) setIntegrated(true);
        if (matched > 0) {
          toast.success(`✅ Updated ${matched} record${matched !== 1 ? 's' : ''}${unmatched > 0 ? ` — ${unmatched} unmatched doc #${unmatched !== 1 ? 's' : ''}` : ''}`);
        } else {
          toast.error(`No matching records found (${unmatched} unmatched doc #)`);
        }
        onRecordsUpdated?.();
      } catch (error: any) {
        toast.error(error.message || "Failed to save");
      } finally {
        setIsSaving(false);
      }
      return;
    }

    const readyRows = rows.filter(r => r.status === 'ready' && r.seller_name);
    if (readyRows.length === 0) {
      toast.error("No rows to save");
      return;
    }

    setIsSaving(true);
    try {
      const records = readyRows.map(r => ({
        county: county.toLowerCase(),
        document_number: r.doc_number || `${county.toUpperCase()}-${r.filing_date}-${r.seller_name.replace(/[^a-zA-Z]/g, '').slice(0, 10)}`,
        filing_date: normalizeDate(r.filing_date),
        document_type: 'Land Contract',
        grantor_name: r.seller_name.toUpperCase(),
        grantor_address: r.address || null,
        is_potential_owner_finance: true,
        status: r.address ? 'reviewed' : 'new',
        address_extraction_method: r.address ? 'courthouse_handwritten' : null,
        search_date: new Date().toISOString(),
      }));

      // Check for duplicates before inserting
      let saved = 0;
      let skipped = 0;

      for (let i = 0; i < records.length; i++) {
        const rec = records[i];
        
        // Try exact match first
        let { data: existing } = await supabase
          .from('county_research')
          .select('id, grantor_name')
          .eq('county', county.toLowerCase())
          .eq('filing_date', rec.filing_date)
          .ilike('grantor_name', rec.grantor_name)
          .is('deleted_at', null)
          .maybeSingle();

        // Fallback: match by last name prefix (handles "SMITH, JOHN" vs "SMITH, JOHN A.")
        if (!existing) {
          const lastName = rec.grantor_name.includes(',')
            ? rec.grantor_name.split(',')[0].trim()
            : rec.grantor_name.split(/\s+/).pop()?.trim();
          if (lastName && lastName.length > 1) {
            const { data: fallback } = await supabase
              .from('county_research')
              .select('id, grantor_name')
              .eq('county', county.toLowerCase())
              .eq('filing_date', rec.filing_date)
              .ilike('grantor_name', `${lastName}%`)
              .is('deleted_at', null)
              .limit(1);
            if (fallback && fallback.length === 1) {
              existing = fallback[0];
              console.log(`Fallback match: "${rec.grantor_name}" → "${existing.grantor_name}"`);
            }
          }
        }

        if (existing) {
          if (rec.grantor_address) {
            await supabase
              .from('county_research')
              .update({
                grantor_address: rec.grantor_address,
                address_extraction_method: 'courthouse_handwritten',
                address_needs_review: false,
                status: 'reviewed',
              })
              .eq('id', existing.id);
            saved++;
          } else {
            skipped++;
          }
        } else {
          const { error } = await supabase.from('county_research').insert(rec);
          if (error) {
            console.error(`Insert error for "${rec.grantor_name}":`, error);
          } else {
            saved++;
          }
        }
      }

      // Mark all ready rows as saved
      setRows(prev => prev.map(r => r.status === 'ready' ? { ...r, status: 'saved' as const } : r));
      setTotalSaved(prev => prev + saved);
      setIntegrated(true);
      toast.success(`✅ Saved ${saved} records${skipped > 0 ? `, ${skipped} duplicates skipped` : ''}`);
      onRecordsUpdated?.();
    } catch (error: any) {
      toast.error(error.message || "Failed to save");
    } finally {
      setIsSaving(false);
    }
  };


  const removeRow = (index: number) => {
    setRows(prev => prev.map((r, i) => i === index ? { ...r, status: 'removed' as const } : r));
  };

  const startEdit = (index: number, field: 'seller_name' | 'address' | 'filing_date' | 'doc_number') => {
    setEditingIndex(index);
    setEditField(field);
    setEditValue(
      field === 'address' ? (rows[index].address || '')
      : field === 'seller_name' ? rows[index].seller_name
      : field === 'doc_number' ? (rows[index].doc_number || '')
      : rows[index].filing_date
    );
  };

  const saveEdit = () => {
    if (editingIndex === null) return;
    setRows(prev => prev.map((r, i) => {
      if (i !== editingIndex) return r;
      if (editField === 'address') return { ...r, address: editValue.trim() || null };
      if (editField === 'seller_name') return { ...r, seller_name: editValue.trim() };
      if (editField === 'filing_date') return { ...r, filing_date: editValue.trim() };
      if (editField === 'doc_number') return { ...r, doc_number: editValue.trim() || undefined, status: r.status === 'unmatched' ? 'ready' : r.status };
      return r;
    }));
    setEditingIndex(null);
    setEditValue("");
  };

  const handleEditKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') { e.preventDefault(); saveEdit(); }
    if (e.key === 'Escape') { setEditingIndex(null); setEditValue(""); }
  };

  const handleNextSheet = () => {
    setImageData(null);
    setRows([]);
    setIntegrated(false);
    setEditingIndex(null);
  };

  const handleClear = () => {
    setRows([]);
    setImageData(null);
    setIntegrated(false);
    toast.info("Cleared (database not affected)");
  };

  const readyCount = rows.filter(r => r.status === 'ready').length;
  const savedCount = rows.filter(r => r.status === 'saved').length;
  const unmatchedCount = rows.filter(r => r.status === 'unmatched').length;
  const isDocMatch = DOC_MATCH_COUNTIES.includes(county.toLowerCase());

  return (
    <Card className="p-6 space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Camera className="h-5 w-5" />
            {isDocMatch ? "Scan Doc # + Address Sheet" : "Scan Courthouse Handwritten Sheet"}
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            {isDocMatch
              ? <>Upload photos of handwritten <strong>Doc # + Address</strong> sheets from {countyLabel}. Each row is matched to an existing record by document number (liber-page) and the seller address is filled in.</>
              : <>Upload photos of handwritten sheets from {countyLabel}. All data (dates, names, addresses) will be extracted and <strong>created as new records</strong>.</>}
          </p>
        </div>
        {rows.length > 0 && !integrated && (
          <Button variant="outline" size="sm" onClick={handleClear} className="text-muted-foreground shrink-0">
            <Trash2 className="mr-2 h-4 w-4" />
            Clear
          </Button>
        )}
      </div>

      {sheetsScanned > 0 && (
        <div className="bg-muted/50 rounded-lg p-3 flex items-center gap-4 text-sm">
          <span className="font-medium">Session Progress:</span>
          <Badge variant="secondary">{sheetsScanned} sheet{sheetsScanned !== 1 ? 's' : ''} scanned</Badge>
          <Badge variant="default" className="bg-green-600">{totalSaved} total saved</Badge>
        </div>
      )}

      <div
        className="border-2 border-dashed border-border rounded-lg p-8 text-center focus:ring-2 focus:ring-ring focus:outline-none"
        onPaste={handlePaste}
        tabIndex={0}
      >
        {!imageData ? (
          <div className="space-y-3">
            <Camera className="h-12 w-12 mx-auto text-muted-foreground" />
            <p className="text-muted-foreground">
              {sheetsScanned > 0
                ? `Ready for sheet ${sheetsScanned + 1} — upload or paste next photo`
                : "Upload a photo of the handwritten courthouse sheet"}
            </p>
            <div className="flex gap-2 justify-center">
              <label>
                <input type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFileUpload} />
                <Button variant="outline" size="sm" asChild>
                  <span><Upload className="mr-2 h-4 w-4" />Upload Photo</span>
                </Button>
              </label>
              <label>
                <input type="file" accept="image/*" className="hidden" onChange={handleFileUpload} />
                <Button variant="outline" size="sm" asChild>
                  <span>📁 Choose File</span>
                </Button>
              </label>
            </div>
            <p className="text-xs text-muted-foreground">Or click here and press Ctrl+V to paste</p>
          </div>
        ) : (
          <div className="space-y-4">
            <img src={imageData} alt="Courthouse sheet" className="max-h-96 mx-auto rounded border" />
            <div className="flex gap-2 justify-center">
              {rows.length === 0 ? (
                <>
                  <Button onClick={handleScan} disabled={isProcessing} size="lg">
                    {isProcessing ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Reading Handwriting...</>
                    ) : (
                      <><CheckCircle className="mr-2 h-4 w-4" />Scan & Preview</>
                    )}
                  </Button>
                  <Button variant="outline" onClick={() => setImageData(null)} disabled={isProcessing}>
                    Clear
                  </Button>
                </>
              ) : (
                <>
                  {!integrated && readyCount > 0 && (
                    <Button onClick={handleSaveAll} disabled={isSaving} size="lg" className="bg-green-600 hover:bg-green-700">
                      {isSaving ? (
                        <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</>
                      ) : (
                        <><Database className="mr-2 h-4 w-4" />{isDocMatch ? `Match & Update ${readyCount} Addresses` : `Save ${readyCount} Records`}</>
                      )}
                    </Button>
                  )}
                  <Button onClick={handleNextSheet} size="lg" variant={integrated ? "default" : "outline"}>
                    <ArrowRight className="mr-2 h-4 w-4" />
                    Scan Next Sheet
                  </Button>
                  {onViewUpdatedRecords && integrated && (
                    <Button variant="outline" onClick={onViewUpdatedRecords}>
                      View Records
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {rows.length > 0 && (
        <div className="space-y-3">
          <div className="flex gap-2 flex-wrap items-center">
            {!integrated && readyCount > 0 && (
              <Badge variant="outline" className="text-amber-600 border-amber-300">{readyCount} Ready</Badge>
            )}
            {savedCount > 0 && (
              <Badge variant="default" className="bg-green-600">✅ {savedCount} {isDocMatch ? 'Matched & Updated' : 'Saved'}</Badge>
            )}
            {unmatchedCount > 0 && (
              <Badge variant="destructive">⚠️ {unmatchedCount} Unmatched Doc #</Badge>
            )}
            <span className="text-xs text-muted-foreground ml-2">
              {integrated
                ? (isDocMatch ? "Addresses filled in on matched records." : "Records saved to database.")
                : (isDocMatch ? "⚠️ Not yet saved — review Doc # + Address, then click Match & Update" : "⚠️ Not yet saved — review and click Save")}
            </span>
          </div>

          <div className="max-h-[500px] overflow-y-auto border rounded">
            <table className="w-full text-sm">
              <thead className="bg-muted sticky top-0">
                <tr>
                  <th className="text-left p-2 w-8">#</th>
                  {isDocMatch && <th className="text-left p-2 w-32">Doc #</th>}
                  <th className="text-left p-2 w-24">Filing Date</th>
                  <th className="text-left p-2">Seller Name</th>
                  <th className="text-left p-2">Address</th>
                  <th className="text-left p-2 w-20">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  if (r.status === 'removed') return null;
                  const rowBg =
                    r.status === 'saved' ? 'bg-green-50'
                    : r.status === 'unmatched' ? 'bg-red-50'
                    : r.status === 'ready' ? 'bg-amber-50'
                    : '';
                  return (
                    <tr key={i} className={`border-t ${rowBg}`}>
                      <td className="p-2 text-muted-foreground">{r.row_number}</td>
                      {isDocMatch && (
                        <td className="p-2 font-mono text-xs">
                          {editingIndex === i && editField === 'doc_number' ? (
                            <Input value={editValue} onChange={e => setEditValue(e.target.value)} onKeyDown={handleEditKeyDown} className="h-7 text-xs" autoFocus />
                          ) : (
                            <span className="cursor-pointer hover:underline" onClick={() => !integrated && startEdit(i, 'doc_number')} title={r.errorMessage || ''}>
                              {r.doc_number || '—'}
                              {r.status === 'unmatched' && <span className="ml-1 text-red-600">⚠</span>}
                            </span>
                          )}
                        </td>
                      )}
                      <td className="p-2 font-mono text-xs">
                        {editingIndex === i && editField === 'filing_date' ? (
                          <Input value={editValue} onChange={e => setEditValue(e.target.value)} onKeyDown={handleEditKeyDown} className="h-7 text-xs" autoFocus />
                        ) : (
                          <span
                            className={`cursor-pointer hover:underline ${!r.filing_date ? 'text-muted-foreground italic' : ''}`}
                            onClick={() => !integrated && !isDocMatch && startEdit(i, 'filing_date')}
                          >
                            {r.filing_date || (isDocMatch && r.status !== 'saved' ? '(from match)' : '—')}
                          </span>
                        )}
                      </td>
                      <td className="p-2">
                        {editingIndex === i && editField === 'seller_name' ? (
                          <Input value={editValue} onChange={e => setEditValue(e.target.value)} onKeyDown={handleEditKeyDown} className="h-7 text-xs" autoFocus />
                        ) : (
                          <span
                            className={`cursor-pointer hover:underline ${!r.seller_name ? 'text-muted-foreground italic' : ''}`}
                            onClick={() => !integrated && !isDocMatch && startEdit(i, 'seller_name')}
                          >
                            {r.seller_name || (isDocMatch && r.status !== 'saved' ? '(from match)' : '—')}
                          </span>
                        )}
                      </td>
                      <td className="p-2 text-xs">
                        {editingIndex === i && editField === 'address' ? (
                          <Input value={editValue} onChange={e => setEditValue(e.target.value)} onKeyDown={handleEditKeyDown} className="h-7 text-xs" autoFocus />
                        ) : (
                          <span className={`cursor-pointer hover:underline ${!r.address ? 'text-muted-foreground italic' : ''}`} onClick={() => startEdit(i, 'address')}>
                            {r.address || 'No address'}
                          </span>
                        )}
                      </td>
                      <td className="p-2">
                        {r.status === 'saved' ? (
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        ) : editingIndex === i ? (
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={saveEdit}><Save className="h-3 w-3" /></Button>
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => { setEditingIndex(null); setEditValue(""); }}><X className="h-3 w-3" /></Button>
                          </div>
                        ) : (
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => startEdit(i, 'address')}><Pencil className="h-3 w-3" /></Button>
                            <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={() => removeRow(i)}><Trash2 className="h-3 w-3" /></Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Card>
  );
}

function normalizeDate(dateStr: string): string {
  if (!dateStr) return new Date().toISOString().split('T')[0];
  
  // Try M/D/YY or M/D/YYYY
  const mdyMatch = dateStr.match(/^(\d{1,2})\/?(\d{1,2})\/?(\d{2,4})$/);
  if (mdyMatch) {
    let year = mdyMatch[3];
    if (year.length === 2) {
      const num = parseInt(year);
      year = num > 50 ? `19${year}` : `20${year}`;
    }
    return `${year}-${mdyMatch[1].padStart(2, '0')}-${mdyMatch[2].padStart(2, '0')}`;
  }
  
  // Already YYYY-MM-DD
  if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) return dateStr;
  
  return dateStr;
}
